// Bloc de paramètres de l'écran de séance : les quatre tuiles (Séries / Reps —
// Charge / Tempo) et, entre les deux grilles, le schéma de série.
//
// Le schéma n'apparaît que sur une méthode composée. Ce n'est pas un `if` de confort :
// une série droite n'a pas de paliers, donc rien à dessiner — l'absence de
// `setSchemeSpec` sur l'exercice EST la condition d'affichage.
//
// UNE SEULE RÈGLE DE TAILLE, dans les quatre tuiles : le CHIFFRE est grand, tout ce
// qui l'accompagne est petit. L'unité, le qualificatif ("par haltère", "de lest"),
// le sigle de méthode (RP, DS, BI) ne sont pas la valeur — ils la décrivent. Les
// mettre à la même taille faisait de "~10 kg par haltère" un pavé de 22px là où seul
// le "10" méritait le regard.
//
// Référence visuelle : maquette « Zeyone Fit — Session v8/v10 ».

import type { ReactNode } from "react"
import { T } from "@/components/session/sessionUi"
import type { SessionExercise, SetScheme, SetSchemeMethod } from "@/types/session"
import { describeTempo } from "@/types/session"
import { parseSetsCount, type ChargeDisplay, type ChargeMovement } from "@/lib/exerciseNormalization"

// ═══ MÉTHODES ══════════════════════════════════════════════

// Exporté : l'aperçu de séance nomme la méthode de chaque exercice avec les mêmes mots.
export const METHOD_LABEL: Record<SetSchemeMethod, { short: string; long: string }> = {
  "rest-pause":        { short: "RP", long: "Rest-Pause" },
  "bi-set":            { short: "BI", long: "Bi-set" },
  "drop-set":          { short: "DS", long: "Drop set" },
  "pyramide-montante": { short: "PY", long: "Pyramide montante" },
  cluster:             { short: "CL", long: "Cluster" },
  "myo-reps":          { short: "MY", long: "Myo-Reps" },
}

/**
 * Séparateur entre paliers, dans les reps. Il porte du sens : un "+" dit des
 * mini-séries d'un même effort (rest-pause, cluster), une flèche dit un changement de
 * charge (drop set, pyramide), un " + " espacé dit deux mouvements (bi-set).
 */
function repsSeparator(method: SetSchemeMethod): { text: string; spaced: boolean } {
  switch (method) {
    case "drop-set":
    case "pyramide-montante": return { text: "→", spaced: true }
    case "bi-set":            return { text: "+", spaced: true }
    default:                  return { text: "+", spaced: false }
  }
}

/**
 * Repère court d'un mouvement — les initiales de ses mots ("Assisted Pull-Up" → APU).
 *
 * Le même repère sert dans la tuile Charge et dans la frise : c'est ce qui permet au
 * client de relier « la ligne APU » à « le palier APU » sans relire les noms complets,
 * qui ne tiennent de toute façon ni dans une tuile ni dans un nœud.
 */
export function movementInitials(name: string): string {
  const words = name
    .replace(/\([^)]*\)/g, " ")          // "(banc incliné)" n'est pas le mouvement
    .split(/[\s\-–—/]+/)
    .map(w => w.trim())
    .filter(Boolean)
  if (words.length === 0) return "?"
  if (words.length === 1) return words[0].slice(0, 3).toUpperCase()
  return words.map(w => w[0]).join("").slice(0, 3).toUpperCase()
}

// ═══ TAILLES ═══════════════════════════════════════════════

/** Le chiffre. Le seul élément en grande taille de toute la tuile. */
const Big = ({ children }: { children: ReactNode }) => (
  <span style={{ fontSize:22,fontWeight:600,color:T.txHi,fontVariantNumeric:"tabular-nums" as const,letterSpacing:-0.3 }}>{children}</span>
)
/** Unité, qualificatif, sigle — tout ce qui accompagne le chiffre sans être le chiffre. */
const Sml = ({ children }: { children: ReactNode }) => (
  <span style={{ fontSize:13,fontWeight:600,color:T.t2 }}>{children}</span>
)
/** Séparateur de paliers : ni chiffre ni qualificatif, une articulation. */
const Sep = ({ children }: { children: ReactNode }) => (
  <span style={{ fontSize:15,fontWeight:500,color:T.t3,padding:"0 1px" }}>{children}</span>
)
/**
 * Repli pour une valeur dont on ne comprend pas la structure : ni chiffre mis en
 * avant, ni découpage, le texte brut tel quel — la taille intermédiaire d'avant ce
 * chantier, plutôt que la taille du chiffre (`Big`) qui prétendrait qu'un des nombres
 * de la chaîne est LA valeur, ou la taille du qualificatif (`Sml`) qui l'effacerait.
 */
const Plain = ({ children }: { children: ReactNode }) => (
  <span style={{ fontSize:16,fontWeight:600,color:T.txHi,lineHeight:1.3 }}>{children}</span>
)

// Une flèche ("→", "->") dit toujours une SUITE de valeurs successives — paliers
// d'une méthode composée qui n'a pas (encore) de `SetScheme`. La chercher n'importe
// où dans une charge OU des reps est le signal le plus sûr qu'un seul chiffre ne peut
// pas représenter la valeur : la NOMBRE qui matcherait `LEAD_NUMBER_RE` en premier
// serait arbitraire (le premier palier, ou pire — dans "7-8RM → -10% → 3RM" — un
// nombre de reps-max pris pour un poids).
const ARROW_RE = /→|->/
// Suite de nombres joints par un MÊME séparateur ("6+4+2", "12/10/8/8/10") : mini-séries
// ou paliers dégressifs en attente de schéma. Ne matche PAS "8/jambe" ou "10/côté",
// dont le token après le séparateur n'est pas un nombre — ces qualificatifs restent
// affichés normalement.
const NUMERIC_CHAIN_RE = /^\s*\d+(?:[.,]\d+)?\s*(?:[+\/]\s*\d+(?:[.,]\d+)?\s*)+$/

/**
 * Une valeur "reps" écrite comme une suite — le signe qu'aucun `SetScheme` ne la
 * gouverne encore et que la chaîne JSON brute va atteindre l'écran telle quelle.
 *
 * Spécifique aux REPS : la charge, elle, passe toujours par `computeChargeKg`, dont le
 * champ `basis` dit déjà sans ambiguïté si la valeur a été comprise — pas besoin d'un
 * second détecteur textuel côté charge (voir `chargeIsRaw`).
 */
function repsIsUnresolvedChain(text: string): boolean {
  return ARROW_RE.test(text) || NUMERIC_CHAIN_RE.test(text.trim())
}

/**
 * Une charge que `computeChargeKg` n'a pas su convertir : `basis: "not_applicable"`
 * est exactement ce signal, quel que soit le texte — un digit isolé dans une note
 * libre ("PDC (+ assistance si <6 reps)") ne doit pas plus être mis en avant qu'une
 * suite à flèche ("7-8RM → -10% → 3RM"). Basis différent de "not_applicable" =
 * `computeChargeKg` a produit lui-même le texte affiché, dans un format qu'il contrôle
 * — la mise en évidence du chiffre y est sûre.
 */
function chargeIsRaw(m: ChargeMovement): boolean {
  return m.basis === "not_applicable"
}

// Premier groupe de chiffres d'une valeur : "10", "7-11", "2,5".
const LEAD_NUMBER_RE = /\d+(?:[.,]\d+)?(?:\s*[-–—]\s*\d+(?:[.,]\d+)?)?/

/**
 * Découpe une valeur affichable autour de son chiffre : ce qui précède, le chiffre,
 * ce qui suit. C'est ce qui permet d'appliquer la règle de taille à N'IMPORTE QUELLE
 * longueur de valeur, sans énumérer les formes produites par `computeChargeKg` :
 *
 *   "~10 kg par haltère"  → ~ · 10 · kg par haltère
 *   "assistance ~22 kg"   → assistance ~ · 22 · kg
 *   "7-11 kg"             → · 7-11 · kg
 *   "Poids du corps"      → aucun chiffre : tout reste en petit
 */
function renderValue(text: string): ReactNode {
  const m = LEAD_NUMBER_RE.exec(text)
  if (!m) return <Sml>{text}</Sml>
  const before = text.slice(0, m.index).trim()
  const after = text.slice(m.index + m[0].length).trim()
  // Un "~" qualifie le nombre qui SUIT : l'en séparer donnerait "~ 11 kg", où le tilde
  // flotte. Tout autre préfixe ("assistance") est un mot et garde son espace.
  return (
    <>
      {before && <Sml>{before.endsWith("~") ? before : `${before} `}</Sml>}
      <Big>{m[0]}</Big>
      {after && <Sml> {after}</Sml>}
    </>
  )
}

/** Intercale un séparateur entre les éléments d'une liste. */
function joinNodes(parts: ReactNode[], sep: ReactNode): ReactNode[] {
  return parts.flatMap((p, i) => (i === 0 ? [p] : [<span key={`s${i}`}>{sep}</span>, p]))
}

// ═══ TUILE ═════════════════════════════════════════════════

function ParamCell({ label, children, sub, accent }: { label: string; children: ReactNode; sub?: string; accent: string }) {
  return (
    <div style={{
      background:T.s1,border:`1px solid ${T.line}`,borderRadius:14,padding:"11px 13px 12px",
      minHeight:92,display:"flex",flexDirection:"column" as const,position:"relative" as const,overflow:"hidden" as const,
    }}>
      {/* Halo et filet d'accent — les `::before` / `::after` de la maquette, rendus en
          vrais éléments faute de pseudo-éléments en style inline. */}
      <div style={{ position:"absolute" as const,left:0,right:0,top:0,height:"46%",background:`linear-gradient(180deg,${accent},transparent)`,opacity:0.13,pointerEvents:"none" as const }} />
      <div style={{ position:"absolute" as const,left:0,right:0,top:0,height:1,background:accent,opacity:0.55,pointerEvents:"none" as const }} />
      <div style={{ fontSize:11,fontWeight:600,letterSpacing:1.1,textTransform:"uppercase" as const,color:T.t3,position:"relative" as const }}>{label}</div>
      <div style={{ marginTop:"auto",position:"relative" as const,lineHeight:1.15,wordBreak:"break-word" as const }}>{children}</div>
      {sub && <div style={{ fontSize:12.5,fontWeight:400,lineHeight:1.35,color:accent,opacity:0.92,marginTop:6,position:"relative" as const }}>{sub}</div>}
    </div>
  )
}

// ═══ CONTENU DES TUILES ════════════════════════════════════

/**
 * Le code de tempo est UN jeton, pas un nombre suivi de texte : "30X0" se lit d'un
 * bloc. Le passer à `renderValue` le couperait en "30" + "X0", ce qui ne veut rien
 * dire. Un tempo nommé ("rapide", "max") n'est pas un chiffre et reste en petit.
 */
function TempoValue({ tempo }: { tempo: string }) {
  const code = tempo.trim().toUpperCase()
  return /^[0-9X]{4}$/.test(code) ? <Big>{code}</Big> : <Sml>{tempo}</Sml>
}

function SeriesValue({ ex, scheme }: { ex: SessionExercise; scheme: SetScheme | null }) {
  if (!scheme) return renderValue(ex.sets)
  return <><Big>{parseSetsCount(ex.sets)}</Big> <Sml>{METHOD_LABEL[scheme.method].short}</Sml></>
}

function RepsValue({ ex, scheme }: { ex: SessionExercise; scheme: SetScheme | null }) {
  if (!scheme) {
    // Une suite sans schéma : "12→10→8" ou "12/10/8/8/10" ne sont pas des reps
    // d'une série droite, ce sont les paliers d'une méthode que la migration n'a pas
    // (encore) décrite. Les afficher tel quel plutôt que de mettre en avant un seul
    // chiffre au hasard de la chaîne.
    return repsIsUnresolvedChain(ex.reps) ? <Plain>{ex.reps}</Plain> : renderValue(ex.reps)
  }
  const sep = repsSeparator(scheme.method)
  const parts = scheme.paliers.map((p, i) => <span key={i}>{renderValue(p.reps)}</span>)
  return <>{joinNodes(parts, <Sep>{sep.spaced ? ` ${sep.text} ` : sep.text}</Sep>)}</>
}

function repsSub(scheme: SetScheme | null): string | undefined {
  if (!scheme) return undefined
  // Le sous-titre dit ce qui sépare deux paliers — l'information que la ligne de reps,
  // seule, ne porte pas.
  const transitions = scheme.paliers.map(p => p.transition).filter(t => t != null)
  const pauses = transitions.filter(t => t!.kind === "pause")
  if (pauses.length > 0 && pauses.length === transitions.length) {
    const secs = [...new Set(pauses.map(p => (p as { seconds: number }).seconds))]
    const estimated = pauses.some(p => (p as { estimated?: boolean }).estimated)
    // "~" : la durée vient de la convention de la méthode, pas du programme.
    return secs.length === 1 ? `pause ${estimated ? "~" : ""}${secs[0]} s` : "pauses variables"
  }
  return transitions.length > 0 ? "sans pause" : undefined
}

/**
 * Note d'une charge : d'où sort le nombre.
 *
 * "PDC" quand c'est le poids du corps qui porte l'effort (assistance, lest) — il n'y a
 * alors aucun pourcentage à afficher, la référence n'est pas un 1RM.
 *
 * Sinon le pourcentage, TOUJOURS écrit "NN% 1RM". Le `detail` brut ne l'écrit pas
 * toujours ainsi : il reprend le qualificatif du programme, absent d'un côté du
 * séparateur et présent de l'autre ("80% / 62% 1RM" → "80%" et "62% 1RM"). Deux
 * formats sur les deux lignes d'un même bi-set se lisent comme deux grandeurs
 * différentes ; on normalise donc à l'affichage.
 */
function chargeNote(m: ChargeMovement): string | null {
  if (m.basis === "bodyweight_net" || m.basis === "bodyweight_gross") return "PDC"
  return formatPercent(m.detail)
}

function formatPercent(detail: string | undefined): string | null {
  const m = /(\d+(?:[.,]\d+)?)\s*%/.exec(detail ?? "")
  return m ? `${m[1].replace(",", ".")}% 1RM` : null
}

/**
 * Un repère par mouvement, produit UNE fois et partagé par la tuile Charge et la
 * frise.
 *
 * Chacune tirait le sien de sa propre source — la tuile du nom canonique résolu par la
 * table `exercises`, la frise du nom écrit dans le JSON du programme — et le même
 * mouvement pouvait donc sortir "DBP" à un endroit et "DFP" trois pixels plus bas.
 * Le nom canonique prime : c'est celui que le titre de l'écran affiche.
 */
function movementTags(charge: ChargeDisplay, scheme: SetScheme | null): string[] | null {
  const canonical = charge.movements.map(m => m.label).filter((l): l is string => !!l)
  const fromScheme = (scheme?.paliers ?? []).map(p => p.movement).filter((m): m is string => !!m)
  const source = canonical.length >= fromScheme.length ? canonical : fromScheme
  return source.length > 0 ? source.map(movementInitials) : null
}

/**
 * Charge d'un exercice à plusieurs mouvements (bi-set) : une ligne par mouvement,
 * repère à gauche, charge à droite, sa note dessous.
 *
 * Une valeur unique ne pouvait pas dire deux charges : "assistance ~22 kg / ~41 kg" sur
 * une seule ligne ne disait ni laquelle va avec quel mouvement, ni d'où chacune sort.
 * Chaque ligne portant sa propre note, le "Charge estimée" générique en pied de tuile
 * n'a plus rien à ajouter et disparaît.
 */
function MultiChargeValue({ movements, tags, accent }: { movements: ChargeMovement[]; tags: string[] | null; accent: string }) {
  return (
    <div style={{ display:"flex",flexDirection:"column" as const,gap:7 }}>
      {movements.map((m, i) => (
        <div key={i} style={{
          paddingTop: i > 0 ? 7 : 0,
          borderTop: i > 0 ? `1px solid ${T.line}` : "none",
        }}>
          <div style={{ display:"flex",alignItems:"baseline",justifyContent:"space-between",gap:6 }}>
            <span style={{ fontSize:11,fontWeight:700,letterSpacing:0.6,color:T.t3,flex:"0 0 auto" }}>
              {tags?.[i] ?? movementInitials(m.label ?? "")}
            </span>
            <span style={{ textAlign:"right" as const,minWidth:0 }}>
              {chargeIsRaw(m) ? <Plain>{m.weight}</Plain> : renderValue(m.weight)}
            </span>
          </div>
          {chargeNote(m) && (
            <div style={{ fontSize:11,fontWeight:400,color:accent,opacity:0.92,textAlign:"right" as const,marginTop:1 }}>{chargeNote(m)}</div>
          )}
        </div>
      ))}
    </div>
  )
}

/**
 * Charge affichée. Sur un schéma dont les paliers changent de charge, ce sont les
 * paliers qui s'affichent ("4 → 6 → 8 kg") : c'est précisément ce que la tuile seule,
 * à valeur unique, ne pouvait pas dire.
 */
function chargeCell(
  charge: ChargeDisplay,
  scheme: SetScheme | null,
  tags: string[] | null
): { value: ReactNode; sub?: string } {
  // Bi-set : chaque ligne porte déjà sa note, la tuile n'a pas de sous-titre commun.
  if (charge.movements.length > 1) {
    return { value: <MultiChargeValue movements={charge.movements} tags={tags} accent={T.green} /> }
  }

  const kgs = scheme?.paliers.map(p => p.charge.kg) ?? []
  const stepped =
    scheme != null &&
    kgs.length > 1 &&
    kgs.every(k => k != null) &&
    new Set(kgs).size > 1 &&
    scheme.paliers.every(p => p.charge.basis === "external")

  if (stepped) {
    const perDumbbell = scheme!.paliers.some(p => p.charge.perDumbbell)
    return {
      value: (
        <>
          {joinNodes(kgs.map((k, i) => <Big key={i}>{k}</Big>), <Sep>{" → "}</Sep>)}
          <Sml> kg</Sml>
        </>
      ),
      // Les pourcentages des paliers, dans le même ordre et le même format que partout
      // ailleurs. "par haltère" vaut pour toute la série et vit ici plutôt que dans la
      // valeur, où trois nombres et deux flèches remplissent déjà la ligne.
      sub: [steppedPercents(scheme!), perDumbbell ? "par haltère" : null]
        .filter(Boolean).join(" · ") || undefined,
    }
  }
  // Mouvement unique : "par haltère" vit déjà dans la valeur, l'écho en sous-titre
  // ferait doublon.
  // Le sous-titre ne porte que la note, comme les lignes d'un bi-set : le pourcentage
  // dit déjà que la charge est calculée depuis un 1RM, "Charge estimée" à côté ne
  // faisait que répéter ce que "78% 1RM" énonce. Sur une charge au poids du corps, la
  // note reste "PDC" seul — il n'y a pas de pourcentage à afficher.
  const single = charge.movements[0]
  const value = !single
    ? renderValue("—")
    : chargeIsRaw(single)
      ? <Plain>{single.weight}</Plain>
      : renderValue(single.weight)
  return { value, sub: (single ? chargeNote(single) : null) ?? undefined }
}

/** "60 → 65 → 72% 1RM" — un pourcentage par palier, un seul "% 1RM" en fin de suite. */
function steppedPercents(scheme: SetScheme): string | null {
  const pcts = scheme.paliers.map(p => p.charge.percent1Rm)
  if (pcts.some(p => p == null)) return null
  return `${pcts.join(" → ")}% 1RM`
}

// ═══ FRISE DE PALIERS ══════════════════════════════════════

/** Texte porté par le lien entre deux paliers — la nature de la transition. */
function transitionLabel(t: NonNullable<SetScheme["paliers"][number]["transition"]>): string {
  if (t.kind === "chain") return "enchaîné"
  if (t.kind === "pause") return `${t.estimated ? "~" : ""}${t.seconds} s`
  if (t.deltaKg == null) return "charge"
  return `${t.deltaKg > 0 ? "+" : "−"}${Math.abs(t.deltaKg)} kg`
}

/**
 * Étiquette d'un palier, sous ses reps. Elle ne dit que ce qui VARIE d'un palier à
 * l'autre : le mouvement quand il change (bi-set), la charge quand elle change (drop
 * set, pyramide). Sur un rest-pause, où charge et mouvement sont constants, elle
 * n'apporterait rien et n'est pas affichée.
 *
 * Le repère de mouvement est le MÊME que celui de la tuile Charge — sans quoi le
 * client aurait deux nomenclatures à rapprocher pour un seul exercice.
 */
function palierLabels(scheme: SetScheme, tags: string[] | null): (string | null)[] {
  const movements = scheme.paliers.map(p => p.movement)
  if (movements.some(Boolean) && new Set(movements).size > 1) {
    // Les repères partagés ne s'appliquent que s'il y en a un par palier ; sinon on
    // retombe sur le nom du palier plutôt que d'aligner deux listes de tailles
    // différentes, ce qui décalerait les étiquettes d'un mouvement.
    if (tags && tags.length === scheme.paliers.length) return tags
    return scheme.paliers.map(p => (p.movement ? movementInitials(p.movement) : null))
  }
  const kgs = scheme.paliers.map(p => p.charge.kg)
  if (kgs.every(k => k != null) && new Set(kgs).size > 1) {
    return scheme.paliers.map(p => `${p.charge.kg} kg`)
  }
  return scheme.paliers.map(() => null)
}

/**
 * Frise des paliers. Purement descriptive : elle dit comment la série est construite,
 * elle ne dit pas où en est le client. Aucun palier n'est donc mis en avant — un
 * palier surligné se lirait « vous êtes ici », ce que rien ne suit.
 */
export function SetSchemeRow({ scheme, tags }: { scheme: SetScheme; tags?: string[] | null }) {
  const labels = palierLabels(scheme, tags ?? null)
  return (
    <div style={{ display:"flex",alignItems:"center",gap:10,margin:"8px 2px 0" }}>
      <div style={{ flex:1,display:"flex",alignItems:"center",gap:5,minWidth:0 }}>
        {scheme.paliers.map((p, i) => (
          <div key={i} style={{ display:"contents" }}>
            {i > 0 && (
              <span style={{ flex:"0 0 auto",fontSize:10.5,fontWeight:500,color:T.t3,whiteSpace:"nowrap" as const,padding:"0 1px" }}>
                {transitionLabel(scheme.paliers[i - 1].transition!)}
              </span>
            )}
            <div style={{
              flex:"1 1 0",minWidth:0,display:"flex",alignItems:"baseline",justifyContent:"center",gap:5,
              background:"rgba(148,163,184,0.07)",borderRadius:8,padding:"7px 4px",
            }}>
              <b style={{ fontSize:16,fontWeight:600,fontVariantNumeric:"tabular-nums" as const,color:T.t2 }}>{p.reps}</b>
              {labels[i] && <i style={{ fontSize:11,fontWeight:400,fontStyle:"normal" as const,color:T.t3,whiteSpace:"nowrap" as const }}>{labels[i]}</i>}
            </div>
          </div>
        ))}
      </div>
      <div style={{ flex:"0 0 auto",paddingLeft:10,borderLeft:`1px solid rgba(148,163,184,0.2)`,fontSize:12,fontWeight:500,color:T.t3,whiteSpace:"nowrap" as const }}>
        1 série
      </div>
    </div>
  )
}

// ═══ BLOC COMPLET ══════════════════════════════════════════

export function ExerciseParams({
  ex, scheme, charge,
}: {
  ex: SessionExercise
  scheme: SetScheme | null
  charge: ChargeDisplay
}) {
  const tags = movementTags(charge, scheme)
  const load = chargeCell(charge, scheme, tags)
  const grid = { display:"grid",gridTemplateColumns:"1fr 1fr",gap:10 } as const

  return (
    <>
      <div style={grid}>
        <ParamCell label="Séries" sub={scheme ? METHOD_LABEL[scheme.method].long : "séries droites"} accent={T.cyanBright}>
          <SeriesValue ex={ex} scheme={scheme} />
        </ParamCell>
        <ParamCell label="Reps" sub={repsSub(scheme)} accent={T.pink}>
          <RepsValue ex={ex} scheme={scheme} />
        </ParamCell>
      </div>
      {scheme && <SetSchemeRow scheme={scheme} tags={tags} />}
      <div style={{ ...grid, marginTop:10 }}>
        <ParamCell label="Charge" sub={load.sub} accent={T.green}>{load.value}</ParamCell>
        {/* Le tempo est décrit depuis son code, pas depuis un texte figé par exercice. */}
        <ParamCell label="Tempo" sub={describeTempo(ex.tempo) || undefined} accent={T.violet}>
          <TempoValue tempo={ex.tempo} />
        </ParamCell>
      </div>
    </>
  )
}
